import { useState } from 'react';
import { ChevronRight, ChevronLeft, Sparkles, Clock, Target, Users, Mic, Music, FileText, Check, Loader2, Wand2 } from 'lucide-react';
import { Button } from '../../components/atoms/Button';
import { Input } from '../../components/atoms/Input';
import { Card } from '../../components/atoms/Card';
import type { ProjectPurpose, ProjectTone } from '../../types/database';

interface OnboardingWizardProps {
  onComplete: (data: OnboardingData) => void;
  onCancel: () => void;
  isGenerating?: boolean;
  generationStatus?: string;
}

export type BackgroundMusicMode = 'ai_generated' | 'none' | 'default_soundtrack';

export interface OnboardingData {
  title: string;
  targetDurationMin: number;
  purpose: ProjectPurpose;
  targetAudience: string;
  tone: ProjectTone;
  includeNarration: boolean;
  backgroundMusicMode: BackgroundMusicMode;
  musicGenre: string;
  userCustomPrompt: string;
}

const STEPS = [
  { id: 'basic', title: 'Osnovne Informacije', icon: FileText },
  { id: 'purpose', title: 'Svrha i Cilj', icon: Target },
  { id: 'options', title: 'Opcije Produkcije', icon: Sparkles },
  { id: 'review', title: 'Pregled', icon: Check },
] as const;

export function OnboardingWizard({ onComplete, onCancel, isGenerating = false, generationStatus = '' }: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState<OnboardingData>({
    title: '',
    targetDurationMin: 2,
    purpose: 'promo',
    targetAudience: '',
    tone: 'professional',
    includeNarration: true,
    backgroundMusicMode: 'default_soundtrack',
    musicGenre: 'ambient',
    userCustomPrompt: '',
  });

  const updateData = <K extends keyof OnboardingData>(key: K, value: OnboardingData[K]) => {
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      onComplete(data);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    } else {
      onCancel();
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return data.title.trim().length > 0;
      case 1:
        return true; // Allow empty target audience for demo
      case 2:
        return true;
      case 3:
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Progress Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {STEPS.map((step, idx) => (
              <div key={step.id} className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    idx <= currentStep
                      ? 'bg-blue-600 text-white'
                      : 'bg-white/5 text-slate-500'
                  }`}
                >
                  <step.icon size={18} />
                </div>
                {idx < STEPS.length - 1 && (
                  <div
                    className={`w-16 h-0.5 mx-2 transition-all ${
                      idx < currentStep ? 'bg-blue-600' : 'bg-white/10'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="text-center">
            <h2 className="text-xl font-black text-white uppercase tracking-wider">
              {STEPS[currentStep].title}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Korak {currentStep + 1} od {STEPS.length}
            </p>
          </div>
        </div>

        {/* Step Content */}
        <Card variant="glass" padding="lg" radius="3xl">
          {currentStep === 0 && (
            <StepBasicInfo data={data} updateData={updateData} />
          )}
          {currentStep === 1 && (
            <StepPurpose data={data} updateData={updateData} />
          )}
          {currentStep === 2 && (
            <StepOptions data={data} updateData={updateData} />
          )}
          {currentStep === 3 && (
            <StepReview data={data} />
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
            <Button 
              variant="ghost" 
              onClick={handleBack} 
              leftIcon={<ChevronLeft size={16} />}
              disabled={isGenerating}
            >
              {currentStep === 0 ? 'Odustani' : 'Natrag'}
            </Button>
            <Button
              variant="primary"
              onClick={handleNext}
              disabled={!canProceed() || isGenerating}
              isLoading={isGenerating}
              rightIcon={currentStep === STEPS.length - 1 ? <Wand2 size={16} /> : <ChevronRight size={16} />}
            >
              {currentStep === STEPS.length - 1 ? 'Izradi Video' : 'Dalje'}
            </Button>
          </div>

          {/* Generation Status */}
          {isGenerating && generationStatus && (
            <div className="mt-4 flex items-center justify-center gap-3 p-3 bg-blue-500/10 rounded-xl border border-blue-500/20">
              <Loader2 size={16} className="text-blue-400 animate-spin" />
              <p className="text-sm text-blue-300 font-medium">{generationStatus}</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// Step 1: Basic Info
interface StepProps {
  data: OnboardingData;
  updateData: <K extends keyof OnboardingData>(key: K, value: OnboardingData[K]) => void;
}

function StepBasicInfo({ data, updateData }: StepProps) {
  return (
    <div className="space-y-6">
      <div>
        <Input
          label="Naziv Projekta"
          placeholder="npr., Promo video za Zadar Archipelago"
          value={data.title}
          onChange={(e) => updateData('title', e.target.value)}
        />
      </div>

      <div>
        <label className="block text-[11px] font-bold uppercase tracking-widest text-blue-500 mb-3">
          Ciljana Dužina
        </label>
        <div className="grid grid-cols-5 gap-3">
          {[0.25, 1, 2, 3, 5].map((min) => (
            <button
              key={min}
              onClick={() => updateData('targetDurationMin', min)}
              className={`p-4 rounded-xl border transition-all ${
                data.targetDurationMin === min
                  ? 'bg-blue-600/20 border-blue-500/50 text-white'
                  : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/[0.08]'
              }`}
            >
              <Clock size={20} className="mx-auto mb-2" />
              <span className="text-sm font-bold">{min === 0.25 ? '15s' : `${min} min`}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Step 2: Purpose
function StepPurpose({ data, updateData }: StepProps) {
  const purposes: { value: ProjectPurpose; label: string; desc: string }[] = [
    { value: 'promo', label: 'Promo', desc: 'Promocija proizvoda ili usluge' },
    { value: 'educational', label: 'Edukativni', desc: 'Tutorial ili edukativni sadržaj' },
    { value: 'corporate', label: 'Korporativni', desc: 'Predstavljanje tvrtke' },
    { value: 'social', label: 'Društveni', desc: 'Sadržaj za društvene mreže' },
    { value: 'documentary', label: 'Dokumentarni', desc: 'Dokumentarni stil' },
  ];

  const tones: { value: ProjectTone; label: string }[] = [
    { value: 'professional', label: 'Profesionalni' },
    { value: 'casual', label: 'Opušteni' },
    { value: 'inspirational', label: 'Inspirativni' },
    { value: 'dramatic', label: 'Dramatični' },
    { value: 'humorous', label: 'Humoristički' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-[11px] font-bold uppercase tracking-widest text-blue-500 mb-3">
          Svrha Videa
        </label>
        <div className="grid grid-cols-1 gap-2">
          {purposes.map((p) => (
            <button
              key={p.value}
              onClick={() => updateData('purpose', p.value)}
              className={`p-4 rounded-xl border text-left transition-all ${
                data.purpose === p.value
                  ? 'bg-blue-600/20 border-blue-500/50'
                  : 'bg-white/5 border-white/10 hover:bg-white/[0.08]'
              }`}
            >
              <span className="text-sm font-bold text-white">{p.label}</span>
              <span className="text-xs text-slate-500 ml-2">— {p.desc}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <Input
          label="Ciljana Publika"
          placeholder="npr., Turisti iz Europe, 25-45 godina..."
          value={data.targetAudience}
          onChange={(e) => updateData('targetAudience', e.target.value)}
          leftIcon={<Users size={16} />}
        />
      </div>

      <div>
        <label className="block text-[11px] font-bold uppercase tracking-widest text-blue-500 mb-3">
          Ton Videa
        </label>
        <div className="flex flex-wrap gap-2">
          {tones.map((t) => (
            <button
              key={t.value}
              onClick={() => updateData('tone', t.value)}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                data.tone === t.value
                  ? 'bg-amber-600 text-white'
                  : 'bg-white/5 text-slate-400 hover:bg-white/[0.08]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Step 3: Options
function StepOptions({ data, updateData }: StepProps) {
  const musicGenres = [
    { value: 'ambient', label: 'Ambient' },
    { value: 'electronic', label: 'Electronic' },
    { value: 'orchestral', label: 'Orchestral' },
    { value: 'acoustic', label: 'Acoustic' },
    { value: 'upbeat', label: 'Upbeat' },
    { value: 'cinematic', label: 'Cinematic' },
  ];

  return (
    <div className="space-y-6">
      {/* Narration Toggle */}
      <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-600/20 rounded-lg">
            <Mic size={18} className="text-blue-500" />
          </div>
          <div>
            <p className="text-sm font-bold text-white">Uključi Naraciju</p>
            <p className="text-[10px] text-slate-500">AI generirana naracija za svaki kadar</p>
          </div>
        </div>
        <button
          onClick={() => updateData('includeNarration', !data.includeNarration)}
          className={`w-12 h-6 rounded-full transition-all relative ${
            data.includeNarration ? 'bg-blue-600' : 'bg-white/10'
          }`}
        >
          <div
            className={`w-5 h-5 rounded-full bg-white absolute top-0.5 transition-all ${
              data.includeNarration ? 'left-6' : 'left-0.5'
            }`}
          />
        </button>
      </div>

      {/* Background Music Mode */}
      <div className="p-4 bg-white/5 rounded-xl border border-white/10">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-purple-600/20 rounded-lg">
            <Music size={18} className="text-purple-500" />
          </div>
          <div>
            <p className="text-sm font-bold text-white">Pozadinska Muzika</p>
            <p className="text-[10px] text-slate-500">Odaberite izvor pozadinske muzike</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { value: 'default_soundtrack' as const, label: 'Default Soundtrack', desc: 'Ambient-Hopeful' },
            { value: 'ai_generated' as const, label: 'AI Generirana', desc: 'AI kreira muziku' },
            { value: 'none' as const, label: 'Bez Muzike', desc: 'Bez pozadinske muzike' },
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => updateData('backgroundMusicMode', opt.value)}
              className={`p-3 rounded-lg text-left transition-all ${
                data.backgroundMusicMode === opt.value
                  ? 'bg-purple-600/20 border border-purple-500/50'
                  : 'bg-white/5 border border-white/10 hover:bg-white/[0.08]'
              }`}
            >
              <span className="text-sm font-bold text-white block">{opt.label}</span>
              <span className="text-[10px] text-slate-500">{opt.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Music Genre */}
      {data.backgroundMusicMode === 'ai_generated' && (
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-widest text-purple-500 mb-3">
            Žanr Muzike
          </label>
          <div className="grid grid-cols-3 gap-2">
            {musicGenres.map((g) => (
              <button
                key={g.value}
                onClick={() => updateData('musicGenre', g.value)}
                className={`p-3 rounded-lg text-sm font-bold transition-all ${
                  data.musicGenre === g.value
                    ? 'bg-purple-600 text-white'
                    : 'bg-white/5 text-slate-400 hover:bg-white/[0.08]'
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Custom Prompt */}
      <div>
        <label className="block text-[11px] font-bold uppercase tracking-widest text-blue-500 mb-2">
          Kako želite da vaš video izgleda? (Opcionalno)
        </label>
        <textarea
          value={data.userCustomPrompt}
          onChange={(e) => updateData('userCustomPrompt', e.target.value)}
          placeholder="Opišite detaljno kako želite da vaš video izgleda. Npr: 'Želim video koji prikazuje ljepote Zadra sa dron snimcima plaža, historijskih znamenitosti i lokalne hrane. Ton treba biti inspirativan i pozivati na posjetu.'"
          className="w-full bg-black/40 border border-white/5 rounded-xl p-4 text-sm text-white placeholder:text-slate-600 min-h-[120px] resize-none outline-none focus:border-blue-500/50"
        />
        <p className="text-[10px] text-slate-500 mt-2">
          Što detaljniji opis, to bolje će AI razumjeti vašu viziju.
        </p>
      </div>
    </div>
  );
}

// Step 4: Review
function StepReview({ data }: { data: OnboardingData }) {
  const purposes: Record<ProjectPurpose, string> = {
    promo: 'Promo',
    educational: 'Edukativni',
    corporate: 'Korporativni',
    social: 'Društveni',
    documentary: 'Dokumentarni',
  };

  const tones: Record<ProjectTone, string> = {
    professional: 'Profesionalni',
    casual: 'Opušteni',
    inspirational: 'Inspirativni',
    dramatic: 'Dramatični',
    humorous: 'Humoristički',
  };

  return (
    <div className="space-y-4">
      <div className="p-4 bg-white/5 rounded-xl border border-white/10">
        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
          Pregled Konfiguracije
        </h4>
        
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-400">Naziv:</span>
            <span className="text-white font-bold">{data.title}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Dužina:</span>
            <span className="text-white">
              {data.targetDurationMin === 0.25 ? '15 sekundi' : `${data.targetDurationMin} minuta`}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Svrha:</span>
            <span className="text-white">{purposes[data.purpose]}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Publika:</span>
            <span className="text-white">{data.targetAudience}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Ton:</span>
            <span className="text-white">{tones[data.tone]}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Naracija:</span>
            <span className={data.includeNarration ? 'text-emerald-500' : 'text-slate-500'}>
              {data.includeNarration ? 'Uključena' : 'Isključena'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Muzika:</span>
            <span className={data.backgroundMusicMode !== 'none' ? 'text-purple-500' : 'text-slate-500'}>
              {data.backgroundMusicMode === 'ai_generated'
                ? `AI Generirana (${data.musicGenre})`
                : data.backgroundMusicMode === 'default_soundtrack'
                  ? 'Default Soundtrack'
                  : 'Isključena'}
            </span>
          </div>
          {data.userCustomPrompt && (
            <div className="pt-3 border-t border-white/10">
              <span className="text-slate-400 block mb-1">Vaš opis:</span>
              <span className="text-white text-xs italic">&ldquo;{data.userCustomPrompt}&rdquo;</span>
            </div>
          )}
        </div>
      </div>

      <div className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/20">
        <div className="flex items-center gap-3 mb-2">
          <Wand2 size={20} className="text-blue-400" />
          <p className="text-sm font-bold text-white">AI će odmah kreirati vaš video</p>
        </div>
        <p className="text-xs text-slate-400">
          Klikom na &ldquo;Izradi Video&rdquo;, AI agenti će generirati kompletan scenarij, 
          scene, kadrove sa slikama i naracijom prema vašim željama.
        </p>
      </div>
    </div>
  );
}
